
import { 
    Query, 
    getDocs, 
    onSnapshot, 
    query, 
    where, 
} from 'firebase/firestore';

export const FirestoreUtils = {
    /**
     * Chunks an array into smaller arrays of a specified size (default 30 for Firestore 'in').
     */
    chunkArray<T>(array: T[], size: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    },

    /**
     * Performs a one-time fetch using 'in' query, automatically chunking the input array.
     */
    async getDocsChunked(
        baseQuery: Query,
        fieldName: string | any, // Can be string or result of documentId()
        values: any[],
        chunkSize = 30
    ): Promise<any[]> {
        if (!values || values.length === 0) return [];
        
        const chunks = this.chunkArray(values, chunkSize);
        const promises = chunks.map(chunk => {
            const q = query(baseQuery, where(fieldName, 'in', chunk));
            return getDocs(q);
        });

        const snapshots = await Promise.all(promises);
        return snapshots.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    },

    /**
     * Sets up multiple real-time listeners using 'in' query, automatically chunking the input array.
     * Merges the results and returns a combined unsubscribe function.
     */
    onSnapshotChunked(
        baseQuery: Query,
        fieldName: string | any,
        values: any[],
        callback: (data: any[]) => void,
        chunkSize = 30
    ): () => void {
        if (!values || values.length === 0) {
            callback([]);
            return () => {};
        }

        const chunks = this.chunkArray(values, chunkSize);
        // Initialize an array to hold the latest result from each chunk
        const results: any[][] = new Array(chunks.length).fill([]);

        const unsubscribes = chunks.map((chunk, index) => {
            const q = query(baseQuery, where(fieldName, 'in', chunk));
            return onSnapshot(q, (snapshot) => {
                results[index] = snapshot.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data(),
                    // Preserve metadata for syncing status if available
                    syncing: (doc.metadata as any)?.hasPendingWrites
                }));
                
                // Only trigger callback when all chunks have emitted at least once or on updates
                callback(results.flat());
            });
        });

        return () => unsubscribes.forEach(unsub => unsub());
    }
};
